import { isForbiddenError } from './errorUtils';

class GlobalErrorHandler {
  constructor() {
    this.listeners = new Set();
    this.hasActiveForbiddenError = false;
    this.setupGlobalHandlers();
  }

  setupGlobalHandlers() {
    // Intercept fetch to catch 403 responses
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);

        // Check if response is 403
        if (response.status === 403) {
          const clonedResponse = response.clone();
          try {
            const body = await clonedResponse.json();
            const error = {
              status: 403,
              code: body.code || 'ACCESS_DENIED',
              message: body.message || 'Access denied',
              response: { status: 403, body }
            };
            this.handleForbiddenError(error);
          } catch {
            // If body is not JSON, still handle as 403
            const error = { status: 403, code: 'ACCESS_DENIED', message: 'Access denied' };
            this.handleForbiddenError(error);
          }
        }

        return response;
      } catch (error) {
        if (isForbiddenError(error)) {
          this.handleForbiddenError(error);
        }
        throw error;
      }
    };

    // Intercept XMLHttpRequest for older AJAX calls
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    const self = this;

    XMLHttpRequest.prototype.open = function (...args) {
      this._url = args[1];
      return originalOpen.apply(this, args);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      this.addEventListener('load', function () {
        if (this.status === 403) {
          try {
            const body = JSON.parse(this.responseText);
            const error = {
              status: 403,
              code: body.code || 'ACCESS_DENIED',
              message: body.message || 'Access denied',
              response: { status: 403, body }
            };
            self.handleForbiddenError(error);
          } catch {
            const error = { status: 403, code: 'ACCESS_DENIED', message: 'Access denied' };
            self.handleForbiddenError(error);
          }
        }
      });
      return originalSend.apply(this, args);
    };

    // Listen for unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason && isForbiddenError(event.reason)) {
        this.handleForbiddenError(event.reason);
        event.preventDefault();
      }
    });
  }

  handleForbiddenError(error) {
    this.hasActiveForbiddenError = true;

    // Notify all listeners
    this.listeners.forEach((listener) => {
      listener(error);
    });
  }

  subscribe(listener) {
    this.listeners.add(listener);

    // Don't notify immediately of existing errors - let route changes clear them
    // This prevents the error from persisting across navigation

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  clearError() {
    this.hasActiveForbiddenError = false;
  }
}

// Create singleton instance
const globalErrorHandler = new GlobalErrorHandler();

export default globalErrorHandler;
